import React, { useState } from "react";
import styled from "styled-components";
import { replyPost } from "../api/post";

interface ReplyInputProps {
  postId: number;
  onReplySuccess?: () => void;
  onCancel?: () => void;
}

const ReplyInput: React.FC<ReplyInputProps> = ({
  postId,
  onReplySuccess,
  onCancel,
}) => {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    if (content.length > 200) {
      alert("返信は200文字以内にしてください。");
      return;
    }

    setIsSubmitting(true);
    try {
      await replyPost(postId, content);
      setContent("");
      if (onReplySuccess) {
        onReplySuccess();
      }
    } catch (error) {
      console.error("Failed to reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <TextArea
          placeholder="返信を入力..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          disabled={isSubmitting}
        />
        <Actions>
          <Buttons>
            <Button
              type="submit"
              disabled={!content.trim() || isSubmitting}
              primary
            >
              {isSubmitting ? "送信中..." : "返信"}
            </Button>
            {onCancel && (
              <Button type="button" onClick={onCancel} disabled={isSubmitting}>
                キャンセル
              </Button>
            )}
          </Buttons>
          <Counter isError={content.length > 200}>{content.length}/200</Counter>
        </Actions>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  padding: 12px 24px;
  border-top: 1px solid #eee;
  background-color: #f9f9f9;
  border-radius: 0 0 24px 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6c63ff;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Counter = styled.span<{ isError: boolean }>`
  color: ${({ isError }) => (isError ? "#e74c3c" : "#999")};
  font-size: 12px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button<{ primary?: boolean }>`
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-size: 1rem;
  background: ${({ primary }) => (primary ? "#6c63ff" : "#eee")};
  color: ${({ primary }) => (primary ? "#fff" : "#222")};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default ReplyInput;
